import {
  Assessment,
  Dashboard,
  Group,
  Payment,
  PointOfSale,
  RemoveShoppingCart,
  Store,
  Storefront,
  Warning
} from "@mui/icons-material";
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ReactNode, useState } from "react";
import { useUserStore } from "../../../stores";
import { colorPrimary } from "../../../theme";
import { UserRole } from "../../../utils";
import { ConcessionariaAnaliticoTab } from "../components/ConcessionariaAnaliticoTab";
import { ConcessionariaSinteticoTab } from "../components/ConcessionariaSinteticoTab";
import { InadimplenciaSinteticoTab } from "../components/InadimplenciaSinteticoTab";
import { PagamentoAnaliticoTab } from "../components/PagamentoAnaliticoTab";
import { PagamentoSinteticoTab } from "../components/PagamentoSinteticoTab";
import { RevendasSemComprasSinteticoTab } from "../components/RevendasSemComprasSinteticoTab";
import { SalesDashboardTab } from "../components/SalesDashboardTab";
import { SalesSinteticoTab } from "../components/SalesSinteticoTab";
import { ShopkeeperAnaliticoTab } from "../components/ShopkeeperAnaliticoTab";
import { ShopkeeperSinteticoTab } from "../components/ShopkeeperSinteticoTab";
import { UsuarioAnaliticoTab } from "../components/UsuarioAnaliticoTab";

type RelatorioTab =
  | "sint-dashboard"
  | "sint-anuncios"
  | "sint-concessionarias"
  | "sint-lojistas"
  | "sint-vendas"
  | "sint-pagamentos"
  | "sint-inadimplentes"
  | "sint-sem-compras"
  | "sint-atrasam-pagtos"
  | "anal-anuncios"
  | "anal-vendas"
  | "anal-concessionarias"
  | "anal-lojistas"
  | "anal-usuarios"
  | "anal-canal-avaliador"
  | "anal-pagamentos";

interface MenuItem {
  id: RelatorioTab;
  label: string;
  icon: ReactNode;
  group: UserRole;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

const menuSections: MenuSection[] = [
  {
    title: "Relatórios Sintéticos",
    items: [
      { id: "sint-dashboard", label: "Dashboard", icon: <Dashboard sx={{ fontSize: 20 }} />, group: UserRole.Dealer },
      // { id: "sint-anuncios", label: "Anúncios", icon: <Campaign sx={{ fontSize: 20 }} /> },
      { id: "sint-concessionarias", label: "Concessionárias", icon: <Store sx={{ fontSize: 20 }} />, group: UserRole.Dealer },
      { id: "sint-lojistas", label: "Lojistas", icon: <Storefront sx={{ fontSize: 20 }} />, group: UserRole.Admin },
      { id: "sint-vendas", label: "Vendas", icon: <PointOfSale sx={{ fontSize: 20 }} />, group: UserRole.Dealer },
      { id: "sint-pagamentos", label: "Pagamentos", icon: <Payment sx={{ fontSize: 20 }} />, group: UserRole.Admin },
      { id: "sint-inadimplentes", label: "Pgtos Pendentes", icon: <Warning sx={{ fontSize: 20 }} />, group: UserRole.Admin },
      { id: "sint-sem-compras", label: "Revendas Sem Compras", icon: <RemoveShoppingCart sx={{ fontSize: 20 }} />, group: UserRole.Admin },
      // { id: "sint-atrasam-pagtos", label: "Revendas que Atrasam Pagtos", icon: <Schedule sx={{ fontSize: 20 }} /> },
    ],
  },
  {
    title: "Relatórios Analíticos",
    items: [
      // { id: "anal-anuncios", label: "Anúncios", icon: <Campaign sx={{ fontSize: 20 }} /> },
      // { id: "anal-vendas", label: "Vendas", icon: <PointOfSale sx={{ fontSize: 20 }} /> },
      { id: "anal-concessionarias", label: "Concessionárias", icon: <Store sx={{ fontSize: 20 }} />, group: UserRole.Dealer },
      { id: "anal-lojistas", label: "Lojistas", icon: <Storefront sx={{ fontSize: 20 }} />, group: UserRole.Admin },
      { id: "anal-usuarios", label: "Usuários", icon: <Group sx={{ fontSize: 20 }} />, group: UserRole.Admin },
      // { id: "anal-canal-avaliador", label: "Canal vs Avaliador", icon: <CompareArrows sx={{ fontSize: 20 }} /> },
      { id: "anal-pagamentos", label: "Pagamentos", icon: <Payment sx={{ fontSize: 20 }} />, group: UserRole.Admin },
    ],
  },
];

function SidebarItem({
  item,
  active,
  onClick,
}: {
  item: MenuItem;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1.5}
      px={2}
      py={1.2}
      bgcolor={active ? colorPrimary : "transparent"}
      borderRadius={2}
      mb={0.3}
      onClick={onClick}
      sx={{
        cursor: "pointer",
        color: active ? "white" : "#64748b",
        boxShadow: active ? "0 4px 12px rgba(255, 107, 0, 0.2)" : "none",
        "&:hover": {
          bgcolor: active ? colorPrimary : "#f1f5f9",
        },
        transition: "all 0.2s",
      }}
    >
      {item.icon}
      <Typography fontSize="0.82rem" fontWeight={active ? 700 : 500}>
        {item.label}
      </Typography>
    </Box>
  );
}

export function RelatorioContent() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const [activeTab, setActiveTab] = useState<RelatorioTab>("sint-dashboard");
  const { user } = useUserStore();
  const role = user?.grupoAcesso.id.toString();

  const activeLabel = menuSections
    .flatMap((s) => s.items)
    .find((i) => i.id === activeTab)?.label;

  const activeSection = menuSections.find((s) =>
    s.items.some((i) => i.id === activeTab)
  )?.title;

  return (
    <Box
      display="flex"
      minHeight="100vh"
      bgcolor="#f8fafc"
      flexDirection={isTablet ? "column" : "row"}
    >
      {/* SIDEBAR */}
      <Box
        width={isTablet ? "100%" : 300}
        bgcolor="white"
        borderRight={isTablet ? "none" : "1px solid #e2e8f0"}
        borderBottom={isTablet ? "1px solid #e2e8f0" : "none"}
        position={isTablet ? "relative" : "sticky"}
        top={0}
        height={isTablet ? "auto" : "100vh"}
        overflow="auto"
        flexShrink={0}
      >
        <Box
          p={2.5}
          borderBottom="1px solid #f1f5f9"
          display="flex"
          alignItems="center"
          gap={1.5}
        >
          <Assessment sx={{ fontSize: 28, color: colorPrimary }} />
          <Typography fontWeight={800} fontSize="1.1rem" color="#1e293b">
            Relatórios
          </Typography>
        </Box>

        {menuSections.map((section, idx) => {
        return (
          <Box key={section.title} p={1.5} pt={idx === 0 ? 1.5 : 0}>
          <Typography
            fontSize="0.7rem"
            fontWeight={700}
            color="#94a3b8"
            textTransform="uppercase"
            letterSpacing={1.2}
            px={2}
            py={1}
          >
            {section.title}
          </Typography>

          {section.items.map((item) => {
            if (role && Number(role) <= Number(item.group)) {
              return (
                <SidebarItem
                  key={item.id}
                  item={item}
                  active={activeTab === item.id}
                  onClick={() => setActiveTab(item.id)}
                />
              )
            }
              return null;
          })}

          {idx < menuSections.length - 1 && (
            <Box
              my={1.5}
              mx={2}
              borderBottom="1px solid #f1f5f9"
            />
          )}
        </Box>
        )

        })}
      </Box>

      {/* MAIN CONTENT */}
      <Box flex={1} p={isMobile ? 2 : 4}>
        <Box mb={3}>
          <Typography
            fontSize="0.75rem"
            fontWeight={600}
            color="#94a3b8"
            textTransform="uppercase"
            letterSpacing={1}
          >
            {activeSection}
          </Typography>
          <Typography fontWeight={800} fontSize="1.5rem" color="#1e293b">
            {activeLabel}
          </Typography>
        </Box>

        {activeTab === "sint-dashboard" && <SalesDashboardTab />}
        {activeTab === "sint-vendas" && <SalesSinteticoTab />}
        {activeTab === "sint-concessionarias" && <ConcessionariaSinteticoTab />}
        {activeTab === "sint-lojistas" && <ShopkeeperSinteticoTab />}
        {activeTab === "anal-concessionarias" && <ConcessionariaAnaliticoTab />}
        {activeTab === "anal-lojistas" && <ShopkeeperAnaliticoTab />}
        {activeTab === "sint-pagamentos" && <PagamentoSinteticoTab />}
        {activeTab === "sint-inadimplentes" && <InadimplenciaSinteticoTab />}
        {activeTab === "sint-sem-compras" && <RevendasSemComprasSinteticoTab />}
        {activeTab === "anal-pagamentos" && <PagamentoAnaliticoTab />}
        {activeTab === "anal-usuarios" && <UsuarioAnaliticoTab />}

        {activeTab !== "sint-vendas" && activeTab !== "sint-dashboard" && activeTab !== "sint-concessionarias" && activeTab !== "sint-lojistas" && activeTab !== "anal-concessionarias" && activeTab !== "anal-lojistas" && activeTab !== "sint-pagamentos" && activeTab !== "sint-inadimplentes" && activeTab !== "sint-sem-compras" && activeTab !== "anal-pagamentos" && activeTab !== "anal-usuarios" && (
          <Box
            bgcolor="white"
            borderRadius={3}
            p={4}
            boxShadow="0 2px 8px rgba(0,0,0,0.04)"
            minHeight={400}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography color="#94a3b8" fontSize="0.95rem">
              Conteúdo de "{activeLabel}" será implementado aqui.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
